'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Fragment, useCallback } from 'react'
import { useGetAllBlogsQuery } from '@/api/blogs/get-all-blogs'
import { useGetAllBlogTopicsQuery } from '@/api/blogs/get-all-topics'
import { FooterItem } from '@/features/footer/footer-item'
import { FooterSection } from '@/features/footer/footer-section'
import {
    getLanguagesForRegion,
    getLocaleForRegion,
    Language,
    Locale,
    parseLanguageFromLocale,
    parseRegionFromLocale,
    Region,
} from '@/setup/locales'
import { getPathname, usePathname } from '@/setup/routing'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

export const FooterLanguageSection = () => {
    const router = useRouter()

    const locale = useLocale()
    const currentRegion = parseRegionFromLocale(locale)
    const currentLanguage = parseLanguageFromLocale(locale)

    const pathname = usePathname()
    const rawParams = useParams()
    const qc = useQueryClient()
    const searchParams = useSearchParams()

    const { data: blogs } = useGetAllBlogsQuery()

    const { data: topicsResponse } = useGetAllBlogTopicsQuery()

    const topics = topicsResponse?.data

    const onLocaleSwitch = useCallback(
        (nextLocale: Locale) => {
            let params =
                pathname === '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]' &&
                !rawParams.customSlug
                    ? { ...rawParams, customSlug: [] }
                    : rawParams

            // In order to properly update the params to the localized slug, we need to check it's locales property
            if (pathname.startsWith('/blog/')) {
                if (blogs?.data && topics) {
                    if (pathname.startsWith('/blog/topic/') && rawParams.slug) {
                        const localizedSlug = topics.find(
                            (topic) =>
                                typeof rawParams.slug === 'string' &&
                                Object.values(topic.locales).includes(rawParams.slug)
                        )

                        if (localizedSlug) {
                            params = {
                                ...params,
                                slug: localizedSlug.locales[parseLanguageFromLocale(nextLocale)],
                            }
                        }
                    } else if (pathname.startsWith('/blog/') && rawParams.slugId) {
                        const id =
                            typeof rawParams.slugId === 'string'
                                ? rawParams.slugId.split('-').pop()
                                : null

                        const localizedSlugId = blogs.data.find((blog) => blog.id === id)
                        const localizedSlug =
                            localizedSlugId?.slugs[parseLanguageFromLocale(nextLocale)]
                        const newParam = `${localizedSlug}-${localizedSlugId?.id}`
                        if (localizedSlugId) {
                            params = { ...params, slugId: newParam }
                        }
                    }
                }
            }

            const nextPathname = getPathname(
                pathname,
                // @ts-ignore
                params,
                nextLocale
            )

            const nextSearchParamsStr = searchParams.toString()

            const nextRoute = nextSearchParamsStr
                ? `${nextPathname}?${nextSearchParamsStr}`
                : nextPathname

            router.replace(nextRoute)

            qc.invalidateQueries()

            router.refresh()
        },
        [router, pathname, rawParams, searchParams, qc, blogs, topics]
    )

    const onRegionSwitch = useCallback(
        (nextRegion: Region) => {
            const nextLocale = getLocaleForRegion(nextRegion, currentLanguage as Language)

            onLocaleSwitch(nextLocale)
        },
        [currentLanguage, onLocaleSwitch]
    )

    const onLanguageSwitch = useCallback(
        (nextLanguage: Language) => {
            const nextLocale = getLocaleForRegion(currentRegion as Region, nextLanguage)

            onLocaleSwitch(nextLocale)
        },
        [currentRegion, onLocaleSwitch]
    )

    const t = useTranslations()

    return (
        <Fragment>
            <FooterSection title={t('footer.country.title')}>
                {Object.values(Region).map((region) => {
                    const regionLabel = run((): string => {
                        switch (region) {
                            case Region.AT:
                                return `🇦🇹 ${t('footer.country.austria')}`

                            case Region.DE:
                                return `🇩🇪 ${t('footer.country.germany')}`

                            case Region.GB:
                                return `🇬🇧 ${t('footer.country.unitedKingdom')}`

                            case Region.NL:
                                return `🇳🇱 ${t('footer.country.netherlands')}`
                        }
                    })

                    return (
                        <FooterItem
                            key={'footer-language-section' + region}
                            label={regionLabel}
                            className={cn(region === currentRegion && 'font-medium text-black')}
                            onClick={() => onRegionSwitch(region)}
                        />
                    )
                })}
            </FooterSection>

            <FooterSection title={t('footer.language')}>
                {getLanguagesForRegion(currentRegion as Region).map((language) => {
                    const languageLabel = run((): string => {
                        switch (language) {
                            case Language.DE:
                                return 'Deutsch'

                            case Language.EN:
                                return 'English'

                            case Language.NL:
                                return 'Nederlands'
                        }
                    })

                    return (
                        <FooterItem
                            key={'footer-language-section' + language}
                            label={languageLabel}
                            className={cn(language === currentLanguage && 'font-medium text-black')}
                            onClick={() => onLanguageSwitch(language)}
                        />
                    )
                })}
            </FooterSection>
        </Fragment>
    )
}
